import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import Datepicker from 'vuejs-datepicker';
import { ja } from 'vuejs-datepicker/dist/locale'
import moment from 'moment';
import Modal from "@/assets/common/Modal/Modal.vue";
import { toCamel } from 'snake-camel';
import { AgriFieldOnPlantingResponse, PlantingDetailResponse } from '@/types/APIResponse';
import { Crop, WeightUnit } from '@/types/Component';
import SelectCropModal from '@/assets/common/Modal/SelectCropModal/SelectCropModal.vue';
import SeedlingSelectModal from '@/assets/common/Modal/ItemSelectModal/SeedlingSelectModal/SeedlingSelectModal.vue';
import store from '@/assets/common/store/store';
import { formatGroupedUsageReasons } from '@/assets/common/services';

export type Field = {
    id: string;
    name: string;
    planted_area: number;
    display_start: string | null;
    display_end: string | null;
    using_area: number;
    last_planting: {
        id: string;
        name: string;
    };
    memo: string;
    file_id: Array<string>;
    checked?: boolean;
    isDeletable: boolean;
}

type PlantingPostModel = {
    name: string;
    basic_unit_id: string | null;
    agri_fields: Array<{ id: string, using_area: number, using_seedlings: number }> | null;
    original_agri_fields: Array<{ id: string, updated: string | null }>;
    seedling_id: string | null;
    seedlings_amount: number;
    seedlings_quantity: number;
    seedling_unit_id: string | null;
    crop_id: string | null;
    color: string | null;
    memo: string;
    work_start_date: string | null;
    work_end_date: string | null;
    selected_works: [];
    per_type: string | null;
    total_yield: number | null;
    weight_unit: string | null;
    shipping_start_date: string | null;
    shipping_end_date: string | null;
    cultivation_method: string | null;
    is_hidden: boolean;
    changed_agrifield_ids: string[];
    producer_tenant_id: string | null;
    // このモーダルでは予算・収入見込みを表示/編集しないが、PUT は全項目を上書きするAPIのため、
    // 既存値を省略すると保存のたびに0で消えてしまう。取得時の値をそのまま送り返して保持する。
    workers_budget: number | null;
    machines_budget: number | null;
    chemicals_budget: number | null;
    fertilizers_budget: number | null;
    materials_budget: number | null;
    income_prospect: number | null;
    // 楽観ロック用（新規作成時は未指定）
    updated?: string | null;
}

interface SeedlingUnitType {
    id: string;
    unit: string;
}

type UsingAreaMessage = {
    id: string,
    message: string
}

type Producers = {
    pdId: string;
    pdName: string;
    pdNo: string;
}

type ProducersTenantDetails = {
    tenantId: string;
    tenantName: string;
    authority: string;
}

type ProducersGet = {
    producers: Producers[];
}

export type ProducerPlantingSummary = {
    id: string;
    name: string;
    tenantId: string;
    isActive?: boolean;
}

export type ProducerPlantingItem = {
    producerId: string;
    producerName: string;
    producerTenantId: string;
    isOpen: boolean;
    plantings: ProducerPlantingSummary[];
}

// 集出荷団体（プランを持つテナント）専用の作付マスター画面。
// 既存の PlantingModal（各種一覧画面から呼び出される作付編集ポップアップ）とは
// 完全に独立したコンポーネントであり、PlantingModal 自体には一切手を加えない。
@Component({
    components: {
        'vuejs-datepicker': Datepicker,
        Modal,
        SelectCropModal,
        SeedlingSelectModal
    }
})
export default class CooperativePlantingModal extends Vue {

    @Prop({ default: false })
    isShow!: boolean;

    private apiBaseUrl = '/plantings'
    private apiBaseAdminUrl = '/admin/plantings'
    private producersUrl = '/producers'

    private wariai = 15
    private modeType = '';
    private editTargetId = '';
    private headerTitle = '作付新規作成';
    private isCreateMode = false;
    private isEditMode = false;
    private showFieldModal = false;
    private showCropsModal = false;
    private showSeedlingModal = false;
    private isFormLoading = false
    private selectedSeedlingUnit = '';
    private beforePlantingName = ''
    private loadedPlanting = false
    private defaultWeightUnit = {
        id: null,
        name: 'kg',
        weight_per_unit: 1
    }
    private defaultPerType = '10a'
    private plantingIsHidden = false
    private hiddenSwitchBtn = false
    private isDeleting = false

    private getField: any = {
        id: '',
        name: '',
        plantedArea: 0,
        plantings: []
    }
    private search = '';
    private checkedCrops: Crop | null = null;
    private checkedField: Array<Field> = [];
    private plantedAreaSum = 0;

    private calcCheckedField: Array<Field> = []
    private calcFieldAreaSum = 0

    private defaultPlantingDetail: PlantingDetailResponse = {
        id: '',
        name: '',
        basic_unit_id: null,
        agri_fields: null,
        seedling: null,
        crop: {
            id: '',
            name: '',
            phonetic: ''
        },
        color: 'rgb(255, 53, 53)',
        memo: '',
        work_start_date: null,
        work_end_date: null,
        selected_works: [],
        per_type: this.defaultPerType,
        workers_budget: null,
        machines_budget: null,
        chemicals_budget: null,
        fertilizers_budget: null,
        materials_budget: null,
        income_prospect: null,
        is_hidden: false,
        total_yield: 0,
        weight_unit: this.defaultWeightUnit,
        shipping_start_date: null,
        shipping_end_date: null,
        cultivation_method: ''
    }
    private plantingDetail: PlantingDetailResponse = this.defaultPlantingDetail
    private initPlanting: PlantingDetailResponse = this.defaultPlantingDetail

    private fieldList: Array<Field> = []
    private weightUnits: Array<WeightUnit> = []
    private seedlingUnits: Array<SeedlingUnitType> = []

    private usingAreaMessageList: UsingAreaMessage[] = []
    private changedAgriFieldIds: string[] = [];
    // 楽観チェック用：画面表示時点の作付圃場のスナップショット（未選択になった圃場＝削除、DBにない圃場＝追加として扱う）
    private originalAgriFieldPlantings: Array<{ id: string, updated: string | null }> = [];
    workRecordCultivationRecordFlag = false;

    // 左ウィンドウ（生産者/作付ツリー）
    private producerPlantings: ProducerPlantingItem[] = []
    private originalProducerPlantings: ProducerPlantingItem[] = []
    private searchText = ''
    private checked = false;
    private selectedItem = '';
    private isLoading = false
    private isSpinnerLoading = false

    // 生産者/テナント選択（圃場モーダルと同じ方式）
    private producersList: Producers[] = []
    private producersTenantList: ProducersTenantDetails[] = []
    private tempSelectedProducerId: string | null = null
    private tempSelectedProducerTenantId: string | null = null
    private selectedProducerId: string | null = null
    private selectedProducerTenantId: string | null = null

    ja = ja
    datepicker1 = new Date();
    datepicker2 = new Date();
    datePickerFormat = 'yyyy-MM-dd';

    get checkedSeedling(): boolean {
        return this.plantingDetail.seedling !== null && this.plantingDetail.seedling !== undefined
    }

    get isPlantingDisabled(): boolean {
        return !this.tempSelectedProducerId || !this.tempSelectedProducerTenantId
    }

    get producerPlantingsForDuplicateCheck(): ProducerPlantingSummary[] {
        // 重複チェックは選択中テナント内の作付のみが対象（producerTenantId は行代表値のため producerId で引く）
        const producer = this.originalProducerPlantings.find(p => p.producerId === this.tempSelectedProducerId)
        if (!producer) {
            return []
        }
        return producer.plantings.filter(pl => pl.tenantId === this.tempSelectedProducerTenantId)
    }

    // 圃場リスト
    get searchField(): any {
        const calcFieldList = this.fieldList.filter((field: any) => {
            const start = moment(this.datepicker1).startOf('day')
            const displayStart = moment(field.display_start).startOf('day')
            const displayEnd = moment(field.display_end).startOf('day')
            const end = moment(this.datepicker2).startOf('day')
            const isMoment = (!displayStart.isValid() || displayStart.isSameOrBefore(end)) && (!displayEnd.isValid() || displayEnd.isSameOrAfter(start))

            return field.name.includes(this.search) && isMoment
        })
        return calcFieldList
    }

    // 選択した圃場の合計面積計算
    get checkedFieldAreaSum(): number {
        this.calcFieldAreaSum = 0
        this.calcCheckedField.forEach((item: any) => {
            const arrayA = String(this.calcFieldAreaSum).split(".")
            const arrayB = String(item.planted_area).split(".")
            let calcA = 0
            let calcB = 0
            let calcC = 0
            let calcD = 0
            let resultStr = "0"
            if (arrayA.length > 1 && arrayB.length > 1) {
                const digitA = arrayA[1].length
                const digitB = arrayB[1].length
                if (digitA > digitB) {
                    arrayB[1] = arrayB[1].concat('0'.repeat(digitA - digitB))
                } else {
                    arrayA[1] = arrayA[1].concat('0'.repeat(digitB - digitA))
                }
            }
            calcA = Number(arrayA[0])
            if (arrayA.length > 1) {
                calcB = Number(arrayA[1])
            }
            calcC = Number(arrayB[0])
            if (arrayB.length > 1) {
                calcD = Number(arrayB[1])
            }
            resultStr = String(calcA + calcC)
            if (calcB + calcD != 0) {
                resultStr = resultStr + "." + String(calcB + calcD)
            }
            this.calcFieldAreaSum = Number(resultStr)
        })
        return this.calcFieldAreaSum
    }

    get refs(): any {
        return this.$refs;
    }

    @Watch('isShow')
    onModalVisibilityChanged(val: boolean) {
        this.$emit('update-inert', val)
        if (val) {
            this.searchText = ''
            this.checked = false
            this.selectedItem = ''
            this.editTargetId = ''
            this.modeType = ''
            this.isCreateMode = false
            this.isEditMode = false
            this.headerTitle = '作付新規作成'
            this.tempSelectedProducerId = null
            this.tempSelectedProducerTenantId = null
            this.selectedProducerId = null
            this.selectedProducerTenantId = null
            this.producersTenantList = []
            this.fieldList = []
            this.weightUnits = []
            this.plantingDetail = Object.assign({}, this.defaultPlantingDetail)
            this.checkedCrops = null
            this.checkedField = []
            this.calcCheckedField = []
            this.plantedAreaSum = 0
            this.usingAreaMessageList = []
            this.changedAgriFieldIds = []
            this.getPlantingsByProducers()
            this.getProducers()
        }
    }

    // 生産者/作付を選択するか、追加ボタンを押すまでは右ウィンドウにフォームを表示しない
    get isPlantingSelected(): boolean {
        return !!this.editTargetId || this.isCreateMode
    }

    closeModal() {
        if (store.getters.isInputChanged && this.hiddenSwitchBtn === false) {
            const confirm = window.confirm("変更内容を破棄します。よろしいですか？");
            if (!confirm) {
                return;
            }
            store.dispatch("doUpdateIsInputChanged", false);
        } else {
            this.hiddenSwitchBtn = false
        }
        this.$emit('update:isShow', false);
        this.producerPlantings = [];
        this.originalProducerPlantings = [];
        this.editTargetId = '';
        this.selectedItem = '';
    }

    openSideBar = () => {
        const isResponsive = window.matchMedia('(max-width: 1000px)').matches;
        if (!isResponsive) {
            return;
        }
        if (document.body.classList.contains('newsSide')) {
            document.body.classList.remove('newsSide')
        } else {
            document.body.classList.add('newsSide')
        }
    }

    closeSideBar = () => {
        const isResponsive = window.matchMedia('(max-width: 1000px)').matches;
        if (!isResponsive) {
            return;
        }
        document.body.classList.remove('newsSide')
    }

    // 生産者/作付ツリーの取得
    private async getPlantingsByProducers() {
        this.isLoading = true
        this.isSpinnerLoading = true
        await this.$apiClient.doRequest('get', `${this.apiBaseAdminUrl}/groupedByProducer?with_hidden=${this.checked}`)
            .then((ret) => {
                const producers = ret.map((producer: any) => {
                    const plantings = Array.isArray(producer.plantings)
                        ? producer.plantings
                            .sort((a: any, b: any) => a.name.localeCompare(b.name, 'ja-JP'))
                            .map((planting: any) => ({
                                ...planting,
                                // 生産者が複数テナントを持つ場合、正しいテナントは各作付が個別に保持する
                                // tenantId であり、行全体の producerTenantId ではない
                                isActive: planting.id === this.selectedItem && planting.tenantId === this.tempSelectedProducerTenantId
                            }))
                        : []
                    return { ...producer, plantings }
                })
                this.producerPlantings = this.preserveIsOpenState(producers, this.producerPlantings)
                this.originalProducerPlantings = [...this.producerPlantings]
                this.doSearch()
            })
            .finally(() => {
                this.isLoading = false
                this.isSpinnerLoading = false
            })
    }

    private preserveIsOpenState(newProducers: ProducerPlantingItem[], oldProducers: ProducerPlantingItem[]) {
        return newProducers.map((newProducer) => {
            const oldProducer = oldProducers.find(p => p.producerId === newProducer.producerId)
            return { ...newProducer, isOpen: oldProducers.length === 0 ? true : oldProducer?.isOpen ?? false }
        })
    }

    private toggleProducer(producer: ProducerPlantingItem) {
        const newIsOpen = !producer.isOpen
        producer.isOpen = newIsOpen
        // 検索フィルタ中は producerPlantings と originalProducerPlantings が
        // 別オブジェクト参照になるため、両方に開閉状態を反映する
        const original = this.originalProducerPlantings.find(p => p.producerId === producer.producerId)
        if (original && original !== producer) {
            original.isOpen = newIsOpen
        }
        return producer
    }

    @Watch('checked')
    private toggleHiddenItems() {
        this.getPlantingsByProducers()
    }

    @Watch('searchText')
    private doSearch(): void {
        const search = this.searchText.trim()
        if (!search) {
            this.producerPlantings = [...this.originalProducerPlantings]
            return
        }
        this.producerPlantings = this.originalProducerPlantings
            .map(producer => {
                const isProducerMatch = producer.producerName?.includes(search)
                const filteredPlantings = producer.plantings.filter(p => p.name?.includes(search))
                if (isProducerMatch || filteredPlantings.length > 0) {
                    return { ...producer, plantings: isProducerMatch ? producer.plantings : filteredPlantings }
                }
                return null
            })
            .filter((p): p is ProducerPlantingItem => p !== null)
    }

    private getProducers() {
        this.$apiClient
            .doRequest('get', `${this.producersUrl}`)
            .then(res => {
                this.producersList = (toCamel(res) as ProducersGet).producers as Producers[];
            })
            .catch(error => {
                console.error('Failed to fetch producers:', error);
            });
    }

    private async getProducersTenant() {
        return this.$apiClient
            .doRequest('get', `${this.producersUrl}/producers_tenant/${this.tempSelectedProducerId}`)
            .then(res => {
                this.producersTenantList = toCamel(res.producersTenant) as ProducersTenantDetails[];
                if (this.producersTenantList.length === 1) {
                    this.tempSelectedProducerTenantId = this.producersTenantList[0].tenantId
                }
            })
            .catch(error => {
                console.error('Failed to fetch producers:', error);
            });
    }

    private hasPlantingInput(): boolean {
        return !!(
            this.plantingDetail.name ||
            this.checkedCrops ||
            this.checkedField.length > 0 ||
            this.plantingDetail.memo ||
            this.plantingDetail.work_start_date ||
            this.plantingDetail.work_end_date ||
            this.plantingDetail.basic_unit_id
        )
    }

    private clearPlantingForm() {
        this.plantingDetail = Object.assign({}, this.defaultPlantingDetail)
        this.checkedCrops = null
        this.checkedField = []
        this.calcCheckedField = []
        this.plantedAreaSum = 0
        this.usingAreaMessageList = []
    }

    private async onProducerChange() {
        if (this.hasPlantingInput()) {
            const confirmed = window.confirm('入力した内容をクリアしますよろしいですか？')
            if (!confirmed) {
                this.tempSelectedProducerId = this.selectedProducerId
                return
            }
            this.clearPlantingForm()
        }
        this.tempSelectedProducerTenantId = null
        this.selectedProducerId = this.tempSelectedProducerId
        this.producersTenantList = []
        await this.getProducersTenant()
        // テナントが1件のみの場合は getProducersTenant() 内で自動選択されるため、
        // その場合も明示的にテナント変更時と同じ後続取得を行う必要がある
        this.selectedProducerTenantId = this.tempSelectedProducerTenantId
        if (this.tempSelectedProducerTenantId) {
            this.getFieldList()
            this.getWeightUnits()
        }
        this.inputChanged()
    }

    private onTenantChange() {
        if (this.hasPlantingInput()) {
            const confirmed = window.confirm('入力した内容をクリアしますよろしいですか？')
            if (!confirmed) {
                this.tempSelectedProducerTenantId = this.selectedProducerTenantId
                return
            }
            this.clearPlantingForm()
        }
        this.selectedProducerTenantId = this.tempSelectedProducerTenantId
        this.getFieldList()
        this.getWeightUnits()
        this.inputChanged()
    }

    // tenantId は作付ごとの実際の所属テナント（生産者が複数テナントを持つ場合、
    // producer.producerTenantId は行代表値でしかなく、個々の作付の正しいテナントとは限らない）
    private async selectPlantingItem(plantingId: string, tenantId: string, producer: ProducerPlantingItem) {
        if (plantingId === this.selectedItem && this.tempSelectedProducerTenantId === tenantId) {
            return
        }
        if (store.getters.isInputChanged && !window.confirm('変更内容を破棄します。よろしいですか？')) {
            return
        }
        this.closeSideBar()
        this.$store.dispatch('doUpdateIsInputChanged', false)
        this.producerPlantings.forEach(p => {
            p.plantings.forEach(pl => {
                this.$set(pl, 'isActive', pl.id === plantingId && pl.tenantId === tenantId)
            })
        })
        this.selectedItem = plantingId
        this.producersTenantList = []
        this.tempSelectedProducerId = producer.producerId
        this.tempSelectedProducerTenantId = tenantId
        this.selectedProducerId = producer.producerId
        this.selectedProducerTenantId = tenantId
        this.modeType = 'edit'
        this.isCreateMode = false
        this.isEditMode = true
        this.headerTitle = '作付編集'
        this.editTargetId = plantingId
        this.getPlantingDetail(plantingId)
    }

    private addPlanting() {
        if (store.getters.isInputChanged && !window.confirm('変更内容を破棄します。よろしいですか？')) {
            return
        }
        this.closeSideBar()
        this.$store.dispatch('doUpdateIsInputChanged', false)
        this.producerPlantings.forEach(p => p.plantings.forEach(pl => this.$set(pl, 'isActive', false)))
        this.selectedItem = ''
        this.editTargetId = ''
        this.modeType = 'create'
        this.isCreateMode = true
        this.isEditMode = false
        this.headerTitle = '作付新規作成'
        this.beforePlantingName = ''
        this.plantingIsHidden = false
        this.usingAreaMessageList = []
        this.changedAgriFieldIds = []
        this.originalAgriFieldPlantings = []
        this.checkedField = []
        this.calcCheckedField = []
        this.plantedAreaSum = 0
        this.checkedCrops = null
        this.plantingDetail = Object.assign({}, this.defaultPlantingDetail)
        // 「追加」は完全に空の新規作成フォームに戻す（生産者/テナント選択も含めて全クリア）
        this.tempSelectedProducerId = null
        this.tempSelectedProducerTenantId = null
        this.selectedProducerId = null
        this.selectedProducerTenantId = null
        this.producersTenantList = []
        this.fieldList = []
        this.weightUnits = []
        this.loadedPlanting = true
    }

    // 作付詳細単品取得
    private getPlantingDetail(plantingId: string) {
        this.isFormLoading = true
        this.editTargetId = plantingId
        const query = this.tempSelectedProducerTenantId ? `?tenant_id=${this.tempSelectedProducerTenantId}` : ''
        this.$apiClient.doRequest('get', `${this.apiBaseUrl}/${this.editTargetId}${query}`)
            .then((res) => {
                this.initPlanting = res
                this.beforePlantingName = res.name
                this.producersTenantList = res.tenantId
                    ? [{ tenantId: res.tenantId, tenantName: res.tenantName ?? res.tenantId, authority: 'OWNER' }]
                    : []

                this.originalAgriFieldPlantings = res.agri_fields != null
                    ? res.agri_fields.map((af: any) => ({ id: af.id, updated: af.updated ?? null }))
                    : []
                if (res.agri_fields != null) {
                    this.getSeedlingUnits();
                    this.checkedField = res.agri_fields.map((af: any) => ({
                        id: af.id, name: af.name, is_hidden: af.is_hidden, planted_area: af.planted_area, isDeletable: af.isDeletable
                    }))
                    this.calcCheckedField = this.checkedField
                    this.plantedAreaSum = this.checkedFieldAreaSum
                    res.agri_fields = res.agri_fields.map((agriField: any) => ({
                        id: agriField.id,
                        is_hidden: agriField.is_hidden,
                        name: agriField.name,
                        planted_area: agriField.planted_area != null ? this.$apiClient.exponentToDecimal(agriField.planted_area) : null,
                        remaining_area: agriField.remaining_area != null ? this.$apiClient.exponentToDecimal(agriField.remaining_area) : null,
                        using_seedlings: agriField.using_seedlings != null ? this.$apiClient.exponentToDecimal(agriField.using_seedlings) : null,
                        using_area: agriField.using_area != null ? this.$apiClient.exponentToDecimal(agriField.using_area) : null,
                        isDeletable: agriField.isDeletable
                    }))
                }
                this.plantingDetail = res

                if (res.weight_unit && res.weight_unit.id) {
                    this.plantingDetail.weight_unit = res.weight_unit
                } else {
                    this.plantingDetail.weight_unit = this.defaultWeightUnit
                }
                if (!res.per_type) {
                    this.plantingDetail.per_type = this.defaultPerType
                }

                this.plantingIsHidden = res.is_hidden
                this.checkedCrops = res.crop
                this.getFieldList()
                this.getWeightUnits();
                this.loadedPlanting = true
            })
            .finally(() => {
                this.isFormLoading = false
            })
    }

    // 圃場一覧の取得（対象生産者テナントの圃場）
    getFieldList() {
        this.fieldList = []
        const query = this.tempSelectedProducerTenantId ? `?tenant_id=${this.tempSelectedProducerTenantId}` : ''
        this.$apiClient.doRequest('get', `/agri_fields${query}`)
            .then((res) => {
                const agriFields: Array<Field> = res.agri_fields;
                this.fieldList = agriFields.map(item => ({
                    ...item,
                    checked: this.checkedField.some(x => x.id === item.id),
                    isDeletable: this.plantingDetail?.agri_fields?.find((pf: any) => pf.id === item.id)?.isDeletable ?? true
                }))
            })
    }

    // 単位一覧の取得
    getWeightUnits() {
        const tenantParam = this.tempSelectedProducerTenantId ? `&tenant_id=${this.tempSelectedProducerTenantId}` : ''
        this.$apiClient.doRequest('get', `/weight_units?with_hidden=true${tenantParam}`).then(res => {
            const weightUnits: Array<WeightUnit> = res.weight_units;
            this.weightUnits = weightUnits
        }).catch(() => {
            return
        })
    }

    private getSeedlingUnits() {
        // 種苗単位はテナント非依存のグローバルマスタのため tenant_id は不要
        this.$apiClient.doRequest('get', '/plantings/seedling_units').then((res) => {
            const seedlingUnits: Array<SeedlingUnitType> = res.seedling_units;
            this.seedlingUnits = seedlingUnits
            const seedlingUnit = this.seedlingUnits?.find((unit) => unit.id === this.plantingDetail.seedling?.seedling_unit_id)
            this.selectedSeedlingUnit = seedlingUnit ? seedlingUnit?.unit : ""
        }).catch(() => {
            return
        })
    }

    private updateSeedlingUnit(e: any) {
        const unitObj = this.seedlingUnits?.find((unit) => unit.id === e.target.value);
        this.selectedSeedlingUnit = unitObj ? unitObj.unit : "";
        this.inputChanged()
    }

    // 作付の履歴
    checkHistory(field: Field) {
        this.getField = { id: '', name: '', plantedArea: 0, plantings: [] }
        const agriFieldId = field.id
        const query = this.tempSelectedProducerTenantId ? `?tenant_id=${this.tempSelectedProducerTenantId}` : ''
        this.$apiClient.doRequest('get', `/agri_fields/${agriFieldId}${query}`)
            .then((res) => {
                const plantings: any[] = []
                if (res.plantings != null) {
                    res.plantings.forEach((item: any) => {
                        const cultivationPlan = (item.cultivation_plan == null || item.cultivation_plan.work_start == null || item.cultivation_plan.work_end == null) ? '指定なし' : new Date(item.cultivation_plan.work_start).getFullYear() + '年' + (new Date(item.cultivation_plan.work_start).getMonth() + 1) + '月 ～' + new Date(item.cultivation_plan.work_end).getFullYear() + '年' + (new Date(item.cultivation_plan.work_end).getMonth() + 1) + '月'
                        plantings.push({
                            id: item.id,
                            name: item.name,
                            cultivation_plan: cultivationPlan
                        })
                    });
                }

                this.getField = {
                    id: res.id,
                    name: res.name,
                    planted_area: res.planted_area,
                    plantings: plantings
                }
            })
    }

    validate() {
        let errmsg: string[] = []

        if (!this.tempSelectedProducerId) {
            errmsg.push("生産者を選択してください")
        }
        if (this.tempSelectedProducerId && !this.tempSelectedProducerTenantId) {
            errmsg.push("生産者のテナントを選択してください")
        }

        // 名前の入力
        if (!this.plantingDetail.name.trim()) {
            errmsg.push("作付名を入力してください")
        }
        // 名前の長さ
        else if (this.plantingDetail.name.length > 255) {
            errmsg.push(`作付名は255文字以内で入力してください（現在は${this.plantingDetail.name.length}文字）`)
        }
        // 重複チェック
        else if (this.producerPlantingsForDuplicateCheck.some(item => {
            if (this.modeType === 'edit') {
                return item.name == this.plantingDetail.name && this.beforePlantingName != this.plantingDetail.name
            } else {
                return item.name == this.plantingDetail.name
            }
        })) {
            errmsg.push("作付名が重複しています。作付名は他と重複しないものを設定してください")
        }
        if (!this.checkedCrops) {
            errmsg.push("作目を選択してください")
        }
        if (!this.dateFormatCheck(this.plantingDetail.work_start_date) || !this.dateFormatCheck(this.plantingDetail.work_end_date)) {
            errmsg.push("栽培期間を入力してください")
        }
        if (moment(this.plantingDetail.work_start_date).isAfter(moment(this.plantingDetail.work_end_date))) {
            errmsg.push("栽培期間の開始日は終了日以前で設定してください")
        }
        if (this.plantingDetail.cultivation_method && this.plantingDetail.cultivation_method.length > 255) {
            errmsg.push(`栽培法は255文字以内で入力してください（現在は${this.plantingDetail.cultivation_method.length}文字）`)
        }
        if (this.plantingDetail.memo && this.plantingDetail.memo.length > 21844) {
            errmsg.push(`メモは21844文字以内で入力してください（現在は${this.plantingDetail.memo.length}文字）`)
        }

        if (this.usingAreaMessageList.length !== 0) {
            const message = this.usingAreaMessageList.map(uam => (uam.message))
            errmsg = errmsg.concat(message)
        }

        let seedlingSum = 0
        this.plantingDetail.agri_fields?.forEach(af => {
            if (af.using_seedlings !== null && af.using_seedlings !== undefined && af.using_seedlings.toString() !== '') {
                const usageStr = af.using_seedlings.toString().split('.')
                if ((af.using_seedlings < 0) || usageStr[0].length > 7 || (usageStr[1] && usageStr[1].length > 2)) {
                    errmsg.push(`利用品種数は0より大きい整数部7桁小数部2桁までの数値を入力してください（圃場：${af.name}）`)
                }
                seedlingSum += +af.using_seedlings
            }
            else {
                af.using_seedlings = 0
            }
            if (af.using_area === null || af.using_area === undefined || af.using_area <= 0) {
                errmsg.push(`使用面積は0より大きい値を入力してください（圃場：${af.name}）`);
            } else {
                const areaStr = af.using_area.toString().split('.');
                const integerPart = areaStr[0];
                const decimalPart = areaStr[1] || '';
                if (integerPart.length > 7 || decimalPart.length > 2) {
                    errmsg.push(`使用面積は整数部7桁、小数部2桁までの数値を入力してください（圃場：${af.name}）`);
                }
            }
        })

        if (this.plantingDetail.seedling !== null && this.plantingDetail.seedling !== undefined) {
            if (this.plantingDetail.seedling.seedlings_amount.toString() !== '') {
                if (this.plantingDetail.seedling.seedlings_amount < 0) {
                    errmsg.push("品種（種苗）購入金額に0以上を入力してください")
                }
                else if (!(/^\d+$/.test(this.plantingDetail.seedling.seedlings_amount.toString()))) {
                    errmsg.push("品種（種苗）購入金額に整数を入力してください")
                }
            }
            else {
                this.plantingDetail.seedling.seedlings_amount = 0
            }
            if (this.plantingDetail.seedling.seedlings_quantity?.toString() !== '' && this.plantingDetail.seedling.seedlings_quantity !== null) {
                const usageStr = this.plantingDetail.seedling.seedlings_quantity.toString().split('.')
                if ((this.plantingDetail.seedling.seedlings_quantity < 0) || usageStr[0].length > 7 || (usageStr[1] && usageStr[1].length > 2)) {
                    errmsg.push("品種（種苗）購入量は0より大きい整数部7桁小数部2桁までの数値を入力してください")
                }
            }
            else {
                this.plantingDetail.seedling.seedlings_quantity = 0
            }
            if (this.plantingDetail.seedling.seedlings_quantity < seedlingSum) {
                errmsg.push("圃場の利用品種数が品種（種苗）購入量を超えています")
            }
        }
        else {
            this.plantingDetail.seedling = null
        }

        if (errmsg.length > 0) {
            window.alert(errmsg.join('\n'))
            return false
        } else {
            return true;
        }
    }

    save() {
        const isValid = this.validate();
        if (!isValid) {
            return false;
        }
        const caltivationPlan = this.plantingDetail
        const checkedFieldIds = this.plantingDetail.agri_fields
            ? this.plantingDetail.agri_fields.map((item) => ({ id: item.id, using_area: item.using_area, using_seedlings: item.using_seedlings }))
            : []
        const data: PlantingPostModel = {
            name: this.plantingDetail.name,
            basic_unit_id: this.plantingDetail.basic_unit_id,
            agri_fields: checkedFieldIds,
            original_agri_fields: this.originalAgriFieldPlantings,
            seedling_id: this.plantingDetail.seedling ? this.plantingDetail.seedling.id : null,
            seedlings_amount: this.plantingDetail.seedling ? this.plantingDetail.seedling.seedlings_amount : 0,
            seedlings_quantity: this.plantingDetail.seedling ? this.plantingDetail.seedling.seedlings_quantity : 0,
            seedling_unit_id: this.plantingDetail.seedling && this.plantingDetail.seedling.seedling_unit_id !== 'null' ? this.plantingDetail.seedling.seedling_unit_id : null,
            crop_id: this.checkedCrops?.id || null,
            color: this.plantingDetail.color,
            memo: this.plantingDetail.memo,
            work_start_date: caltivationPlan.work_start_date,
            work_end_date: caltivationPlan.work_end_date,
            selected_works: [],
            per_type: caltivationPlan.per_type,
            total_yield: caltivationPlan.total_yield,
            weight_unit: caltivationPlan.weight_unit.id,
            shipping_start_date: caltivationPlan.shipping_start_date,
            shipping_end_date: caltivationPlan.shipping_end_date,
            cultivation_method: this.plantingDetail.cultivation_method,
            // 保存ボタンで現在の非表示状態を上書きしてしまわないよう、既存の状態をそのまま送る
            // （表示/非表示の切り替えは switchDisplayPlanting() 専用の別APIで行う）
            is_hidden: this.plantingIsHidden,
            changed_agrifield_ids: this.changedAgriFieldIds,
            producer_tenant_id: this.tempSelectedProducerTenantId,
            // 予算・収入見込みはこの画面では非表示・編集不可のため、取得済みの値をそのまま送り返して保持する
            workers_budget: this.plantingDetail.workers_budget,
            machines_budget: this.plantingDetail.machines_budget,
            chemicals_budget: this.plantingDetail.chemicals_budget,
            fertilizers_budget: this.plantingDetail.fertilizers_budget,
            materials_budget: this.plantingDetail.materials_budget,
            income_prospect: this.plantingDetail.income_prospect,
            updated: this.plantingDetail.updated
        }

        this.isFormLoading = true
        if (this.modeType === 'edit') {
            const result = window.confirm("内容を上書きして保存します。よろしいですか？")
            if (!result) {
                this.isFormLoading = false
                return false;
            }

            if (this.workRecordCultivationRecordFlag) {
                const result2 = window.confirm("圃場面積を変更した場合、栽培履歴、作業記録で、使用した農薬・肥料の使用量は変更されません。")
                if (!result2) {
                    this.isFormLoading = false
                    return false;
                }
            }

            this.$apiClient.doRequest("put", `${this.apiBaseUrl}/${this.editTargetId}`, data)
                .then((res) => {
                    if (res && res.message) {
                        window.alert(res.message)
                        return
                    }
                    this.$toasted.success('変更を保存しました。')
                    // 保存後の updated を反映しないと、直後の削除/表示切替が誤って
                    // 楽観ロック競合と判定されてしまうため、最新状態を再取得する
                    this.getPlantingDetail(this.editTargetId)
                    this.getPlantingsByProducers()
                    // 失敗時に再試行できるよう、成功した場合のみリセットする
                    this.changedAgriFieldIds = [];
                    this.workRecordCultivationRecordFlag = false;
                    this.$store.dispatch('doUpdateIsInputChanged', false);
                })
                .catch(() => {
                    this.$toasted.error('保存に失敗しました。')
                })
                .finally(() => {
                    this.isFormLoading = false
                })
        } else {
            this.$apiClient.doRequest("post", this.apiBaseUrl, data)
                .then((res) => {
                    if (res && res.message) {
                        window.alert(res.message)
                        return
                    }
                    this.$toasted.success('保存しました。')
                    const targetProducerId = this.tempSelectedProducerId
                    const targetTenantId = this.tempSelectedProducerTenantId
                    this.getPlantingsByProducers().then(() => {
                        const producer = this.producerPlantings.find(p => p.producerId === targetProducerId)
                        if (producer && targetTenantId) {
                            this.selectPlantingItem(res.id, targetTenantId, producer)
                        }
                    })
                    this.changedAgriFieldIds = [];
                    this.workRecordCultivationRecordFlag = false;
                    this.$store.dispatch('doUpdateIsInputChanged', false);
                })
                .catch(() => {
                    this.$toasted.error('保存に失敗しました。')
                })
                .finally(() => {
                    this.isFormLoading = false
                })
        }
    }

    cancel() {
        if (this.$store.getters.isInputChanged) {
            const result = window.confirm('変更内容を破棄します。よろしいですか？');
            if (!result) {
                return false;
            }
        }
        this.changedAgriFieldIds = [];
        this.workRecordCultivationRecordFlag = false;
        this.$store.dispatch('doUpdateIsInputChanged', false);
        if (this.editTargetId) {
            this.getPlantingDetail(this.editTargetId)
        } else {
            this.addPlanting()
        }
    }

    switchDisplayPlanting() {
        const isValid = this.validate();
        if (!isValid) {
            return false;
        }
        const displayMessage = !this.plantingIsHidden ? '非表示にし' : '表示状態に戻し'
        const result = window.confirm(`${this.beforePlantingName}を${displayMessage}ます。よろしいですか？`)
        if (!result) {
            return false;
        }
        this.hiddenSwitchBtn = true;

        this.isFormLoading = true
        const newIsHidden = !this.plantingIsHidden
        const tenantParam = this.tempSelectedProducerTenantId ? `&tenant_id=${this.tempSelectedProducerTenantId}` : ''
        const updatedParam = this.plantingDetail.updated ? `&updated=${encodeURIComponent(this.plantingDetail.updated)}` : ''
        this.$apiClient.doRequest("put", `${this.apiBaseUrl}/${this.editTargetId}/display?is_hidden=${newIsHidden}${tenantParam}${updatedParam}`)
            .then((res) => {
                if (res && res.message) {
                    window.alert(res.message)
                    this.hiddenSwitchBtn = false
                    return
                }
                this.plantingIsHidden = newIsHidden
                this.$toasted.success(newIsHidden ? `非表示に変更しました。` : `表示に変更しました。`)
                // 変更後の updated を反映しないと、直後の削除/保存が誤って
                // 楽観ロック競合と判定されてしまうため、フォーム全体を再取得せず updated だけ反映する
                if (res && res.updated) {
                    this.plantingDetail.updated = res.updated
                }
                this.getPlantingsByProducers()
            })
            .catch(() => {
                this.$toasted.error('変更に失敗しました。')
            })
            .finally(() => {
                this.isFormLoading = false
            })
    }

    private deletePlanting() {
        if (this.isDeleting) {
            return
        }
        const result = window.confirm(`${this.beforePlantingName}を削除します。よろしいですか？`)
        if (!result) {
            return
        }
        this.isDeleting = true
        this.isFormLoading = true
        const params: string[] = []
        if (this.tempSelectedProducerTenantId) {
            params.push(`tenant_id=${this.tempSelectedProducerTenantId}`)
        }
        if (this.plantingDetail.updated) {
            params.push(`updated=${encodeURIComponent(this.plantingDetail.updated)}`)
        }
        const query = params.length > 0 ? `?${params.join('&')}` : ''
        this.$apiClient.doRequest("delete", `${this.apiBaseUrl}/${this.editTargetId}${query}`)
            .then((res) => {
                if (res && res.message) {
                    window.alert(res.message)
                    return
                }
                this.$toasted.success('削除しました。')
                this.$store.dispatch('doUpdateIsInputChanged', false)
                this.editTargetId = ''
                this.selectedItem = ''
                this.isCreateMode = false
                this.isEditMode = false
                this.tempSelectedProducerId = null
                this.tempSelectedProducerTenantId = null
                this.plantingDetail = Object.assign({}, this.defaultPlantingDetail)
                this.getPlantingsByProducers()
            })
            .catch(() => {
                this.$toasted.error('削除に失敗しました。')
            })
            .finally(() => {
                this.isDeleting = false
                this.isFormLoading = false
            })
    }

    openFieldModal() {
        if (!this.dateFormatCheck(this.plantingDetail.work_start_date) || !this.dateFormatCheck(this.plantingDetail.work_end_date)) {
            window.alert("栽培期間を入力してください。")
            return false
        } else {
            this.datepicker1 = moment(this.plantingDetail.work_start_date).toDate()
            this.datepicker2 = moment(this.plantingDetail.work_end_date).toDate()
        }
        this.calcCheckedField = this.checkedField

        this.showFieldModal = true
    }

    async saveFieldModal() {
        const newFieldIds = new Set(this.calcCheckedField.map(cf => cf.id))
        const removedFields = this.modeType === 'edit'
            ? this.checkedField.filter(cf => !newFieldIds.has(cf.id))
            : []

        const blockedGroups: Array<{ name: string, reasons: string[] }> = []
        for (const removed of removedFields) {
            const url = `${this.apiBaseUrl}/check_agri_field_deletable/${this.plantingDetail.id}/${removed.id}`
            const reasons: string[] = await this.$apiClient.doRequest("get", url)
            if (reasons && reasons.length > 0) {
                blockedGroups.push({ name: removed.name, reasons })
            }
        }
        if (blockedGroups.length > 0) {
            window.alert(formatGroupedUsageReasons(blockedGroups))
            this.calcCheckedField = this.checkedField
            return
        }

        this.checkedField = this.calcCheckedField
        this.plantedAreaSum = this.calcFieldAreaSum
        this.search = ''

        const agriFields = this.checkedField.map(af => af.id)
        const workStartDate = this.plantingDetail.work_start_date
        const workEndDate = this.plantingDetail.work_end_date
        if (workStartDate && workEndDate) {
            this.updateUsingArea(agriFields, workStartDate, workEndDate)
        }

        this.showFieldModal = false
    }

    cancelFieldModal() {
        this.calcCheckedField = this.checkedField
        this.showFieldModal = false
        this.search = ''
        this.getField = { id: '', name: '', plantedArea: 0, plantings: [] }
    }

    async selectedFieldDelete(fieldId: string) {
        const existedBeforeRemoval = this.checkedField.some(cf => cf.id === fieldId);
        if (this.modeType == "edit" && existedBeforeRemoval) {
            // このAPIはID指定のみでテナント非依存のため tenant_id は不要
            const url = `${this.apiBaseUrl}/check_agri_field_deletable/${this.plantingDetail.id}/${fieldId}`;
            const reasons: string[] = await this.$apiClient.doRequest("get", url);
            if (reasons && reasons.length > 0) {
                const fieldName = this.checkedField.find(cf => cf.id === fieldId)?.name ?? fieldId
                window.alert(formatGroupedUsageReasons([{ name: fieldName, reasons }]));
                return;
            }
        }
        this.checkedField = this.checkedField.filter(cf => cf.id !== fieldId)
        if (this.plantingDetail.agri_fields) {
            this.plantingDetail.agri_fields = this.plantingDetail.agri_fields.filter(af => af.id !== fieldId)
        }
        this.calcCheckedField = this.checkedField
        this.plantedAreaSum = this.checkedFieldAreaSum
        this.usingAreaMessageList = this.usingAreaMessageList.filter(uam => uam.id !== fieldId)
        this.inputChanged()
    }

    // 品種（種苗）モーダル関連
    openSeedlingModal() {
        this.showSeedlingModal = true
        this.$emit('crop-modal-inert', true)
        this.inputChanged()
    }

    cancelSeedling() {
        const newAgriFields = this.plantingDetail.agri_fields?.map(af => ({ ...af, using_seedlings: 0 }))
        this.plantingDetail = {
            ...this.plantingDetail,
            seedling: null,
            agri_fields: newAgriFields ? newAgriFields : null
        }
        this.selectedSeedlingUnit = '';
        this.inputChanged()
    }

    saveSeedlingModal(seedling: any): void {
        const query = this.tempSelectedProducerTenantId ? `?tenant_id=${this.tempSelectedProducerTenantId}` : ''
        this.$apiClient.doRequest('get', `/seedlings/${seedling.id}${query}`)
            .then((res) => {
                this.plantingDetail = {
                    ...this.plantingDetail,
                    seedling: {
                        id: seedling.id,
                        name: seedling.name,
                        seedlings_quantity: 0,
                        seedlings_amount: 0,
                        seedling_unit_id: null,
                    },
                    crop: { ...res.crop }
                }
                this.selectedSeedlingUnit = '';
                this.checkedCrops = res.crop
                this.getSeedlingUnits()
            })
            .finally(() => {
                this.showSeedlingModal = false
            })
    }

    cancelSeedlingModal() {
        this.showSeedlingModal = false
    }

    // 作目モーダル関連
    openCropsModal() {
        this.showCropsModal = true
        this.$emit('crop-modal-inert', true)
    }
    saveCropsModal(crop: Crop) {
        this.checkedCrops = crop
        this.showCropsModal = false
    }
    cropsModalCancel() {
        this.showCropsModal = false
    }

    @Watch('showCropsModal')
    onCropModalChanged(val: boolean) {
        this.$emit('crop-modal-inert', val)
    }

    @Watch('showSeedlingModal')
    onSeedlingModalChanged(val: boolean) {
        this.$emit('seedling-modal-inert', val)
    }

    updateColor(color: any) {
        this.plantingDetail.color = `rgba(${color.rgba.r},${color.rgba.g},${color.rgba.b})`
    }

    private areaValueToRate(plantedArea: number, area: number): number {
        return Number(plantedArea) !== 0 ? Math.round((area / plantedArea) * 10000) / 100 : 0
    }

    private areaRateToValue(plantedArea: number, areaRate: number) {
        let result = plantedArea * (areaRate / 100)
        let resultStr = String(result)
        if (resultStr.indexOf('.') !== -1) {
            const splitDecimal = resultStr.split('.')
            resultStr = `${splitDecimal[0]}.${splitDecimal[1].substring(0, 9)}`
            result = Number(resultStr)
        }
        return result
    }

    private updateWorkStartDate(e: Event) {
        const target = e.target
        if (target instanceof HTMLInputElement) {
            const agriFields = this.plantingDetail.agri_fields?.map(af => af.id)
            this.plantingDetail.work_start_date = target.value
            if (this.plantingDetail.work_start_date && this.plantingDetail.work_end_date && agriFields && agriFields.length !== 0) {
                this.updateUsingArea(agriFields, this.plantingDetail.work_start_date, this.plantingDetail.work_end_date)
            }
        }
        this.inputChanged()
    }

    private updateWorkEndDate(e: Event) {
        const target = e.target
        if (target instanceof HTMLInputElement) {
            const agriFields = this.plantingDetail.agri_fields?.map(af => af.id)
            this.plantingDetail.work_end_date = target.value
            if (this.plantingDetail.work_start_date && this.plantingDetail.work_end_date && agriFields && agriFields.length !== 0) {
                this.updateUsingArea(agriFields, this.plantingDetail.work_start_date, this.plantingDetail.work_end_date)
            }
        }
        this.inputChanged()
    }

    private updateAreaRate(index: number, event: Event) {
        const target = event.target
        if (target instanceof HTMLInputElement && this.plantingDetail.agri_fields !== null) {
            const agriField = this.plantingDetail.agri_fields[index]
            const value = Number(target.value)
            const maxRate = Math.floor(this.areaValueToRate(agriField.planted_area, agriField.remaining_area))
            agriField.using_area = maxRate === value ? agriField.remaining_area : this.areaRateToValue(agriField.planted_area, value)
            this.usingAreaMessageList = this.usingAreaMessageList.filter(uam => uam.id !== agriField.id)
        }
    }

    private updateAreaValue(index: number, event: Event) {
        const target = event.target
        if (target instanceof HTMLInputElement && this.plantingDetail.agri_fields !== null) {
            const agriField = this.plantingDetail.agri_fields[index]
            const usingArea = Number(target.value)
            this.usingAreaMessageList = this.usingAreaMessageList.filter(uam => uam.id !== agriField.id)
            this.checkArea(agriField, usingArea)
        }
    }

    private checkArea(agriField: AgriFieldOnPlantingResponse, usingArea: number) {
        if (agriField.remaining_area < usingArea) {
            const message = `${agriField.name}における利用面積が圃場の空き面積を超えています`
            const usingAreaMessage = { id: agriField.id, message }
            this.usingAreaMessageList.push(usingAreaMessage)
        }
    }

    private validateArea(usingArea: string, remainingArea: string): boolean {
        return Number(remainingArea) < Number(usingArea);
    }

    private async getRemainingAreas(agriFields: string[], workStartDate: string, workEndDate: string) {
        if (agriFields.length !== 0) {
            const agriFieldsParam = 'agri_fields=' + agriFields.reduce((paramStr, af) => `${paramStr}&agri_fields=${af}`)
            const workStartDateParam = `work_start_date=${workStartDate}`
            const workEndDateParam = `work_end_date=${workEndDate}`
            const excludePlantingParam = this.modeType === 'edit' && this.editTargetId !== '' ? `exclude_planting_id=${this.editTargetId}` : ''
            const tenantParam = this.tempSelectedProducerTenantId ? `tenant_id=${this.tempSelectedProducerTenantId}` : ''
            const uri = `/agri_fields/remaining_areas?${agriFieldsParam}&${workStartDateParam}&${workEndDateParam}&${excludePlantingParam}&${tenantParam}`
            const encodedUri = encodeURI(uri)
            const resAgriFields: AgriFieldOnPlantingResponse[] = await this.$apiClient.doRequest('get', encodedUri)
            return resAgriFields
        } else {
            return []
        }
    }

    private async updateUsingArea(agriFields: string[], workStartDate: string, workEndDate: string) {
        const newAgriFields = await this.getRemainingAreas(agriFields, workStartDate, workEndDate)
        if (this.plantingDetail.agri_fields === null) {
            this.plantingDetail.agri_fields = []
        }
        this.usingAreaMessageList = []
        newAgriFields.forEach((naf, index) => {
            const oldAgriField = this.plantingDetail.agri_fields?.find(oaf => oaf.id === naf.id)
            const usingArea = oldAgriField
                ? oldAgriField.using_area
                : 0
            const usingSeedlings = oldAgriField ? oldAgriField.using_seedlings : 0;
            const isDeletable = oldAgriField ? oldAgriField.isDeletable : true
            newAgriFields[index] = { ...naf, using_area: usingArea, isDeletable: isDeletable, using_seedlings: usingSeedlings }
            this.checkArea(naf, usingArea)
        })
        this.plantingDetail.agri_fields?.splice(0, this.plantingDetail.agri_fields.length)
        this.plantingDetail.agri_fields?.push(...newAgriFields)
        this.plantingDetail.agri_fields.sort((a: any, b: any) => a.name.localeCompare(b.name));
    }

    private dateFormatCheck(date: string | null) {
        const regex = new RegExp(/^\d{4}-\d{2}-\d{2}$/)
        return date !== null && regex.test(date)
    }

    inputChanged() {
        if (store.getters.isInputChanged == false) {
            this.$store.dispatch('doUpdateIsInputChanged', true);
        }
    }

    private handlePlantedarea(index: number) {
        const agriFields = this.plantingDetail.agri_fields;
        if (!agriFields || index < 0 || index >= agriFields.length) {
            return;
        }
        const agriFieldId = agriFields[index].id;

        if (!this.changedAgriFieldIds.includes(agriFieldId)) {
            this.changedAgriFieldIds.push(agriFieldId);
        }

        this.workRecordCultivationRecordFlag = agriFields.some(field =>
            this.changedAgriFieldIds.includes(field.id) &&
            field.isDeletable === false
        );
        this.inputChanged();
    }
}
